import { useSyncExternalStore } from 'react'
import { storage } from '../api'
import { en } from './en'
import { ru } from './ru'

export type Lang = 'ru' | 'en'
export type Vars = Record<string, string | number | boolean | undefined>
/** A message is text with {name} placeholders, or a function for plurals. */
export type Msg = string | ((v: Vars) => string)
export type MessageKey = keyof typeof ru
export type Messages = Record<MessageKey, Msg>

export const LANGS: { code: Lang; label: string; name: string }[] = [
  { code: 'ru', label: 'RU', name: 'Русский' },
  { code: 'en', label: 'EN', name: 'English' },
]

const dicts: Record<Lang, Messages> = { ru, en }
const LANG_KEY = 'chillio.lang'

/** Picks the saved language, or the first supported one the system prefers. */
function detect(): Lang {
  const saved = storage.get(LANG_KEY)
  if (saved === 'ru' || saved === 'en') return saved
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const tag of prefs) {
    const base = tag?.toLowerCase().split('-')[0]
    if (base === 'ru' || base === 'en') return base
  }
  return 'en'
}

let lang: Lang = detect()
const listeners = new Set<() => void>()
document.documentElement.lang = lang

export function getLang() {
  return lang
}

export function setLang(next: Lang) {
  storage.set(LANG_KEY, next)
  apply(next)
}

function apply(next: Lang) {
  if (next === lang) return
  lang = next
  document.documentElement.lang = next
  listeners.forEach((fn) => fn())
}

/**
 * Follows a locale the host app reports (Discord), unless the user picked
 * a language here: their choice wins, and it is not saved as one.
 */
export function followLocale(locale: string) {
  if (storage.get(LANG_KEY)) return
  const base = locale.toLowerCase().split('-')[0]
  apply(base === 'ru' ? 'ru' : 'en')
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Translates a key in the current language. */
export function t(key: MessageKey, vars: Vars = {}): string {
  const msg = dicts[lang][key] ?? dicts.en[key]
  if (typeof msg === 'function') return msg(vars)
  return msg.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? ''))
}

export { plural } from './plural'

/** Re-renders the component when the language changes. */
export function useI18n() {
  const current = useSyncExternalStore(subscribe, getLang)
  return { t, lang: current, setLang }
}

export function formatRate(bps: number) {
  if (bps >= 1 << 20) return t('unit.mbps', { v: (bps / (1 << 20)).toFixed(1) })
  return t('unit.kbps', { v: Math.round(bps / 1024) })
}

export function formatSize(bytes: number) {
  if (bytes >= 1 << 30) return t('unit.gb', { v: (bytes / (1 << 30)).toFixed(1) })
  return t('unit.mb', { v: Math.round(bytes / (1 << 20)) })
}
